import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { useCreateContact } from '@/api/contacts'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated?: (phoneNumber: string) => void
}

export function AddContactDialog({ open, onOpenChange, onCreated }: Props) {
  const { t } = useTranslation()
  const createContact = useCreateContact()

  const [phoneNumber, setPhoneNumber] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')

  const reset = () => {
    setPhoneNumber('')
    setFirstName('')
    setLastName('')
  }

  const handleClose = () => {
    if (createContact.isPending) return
    reset()
    onOpenChange(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmedPhone = phoneNumber.trim()
    if (!trimmedPhone) {
      toast.error(t('contacts.add.phoneRequired', 'Numéro requis'))
      return
    }
    if (!firstName.trim() && !lastName.trim()) {
      toast.error(t('contacts.add.nameRequired', 'Prénom ou nom requis'))
      return
    }
    try {
      const created = await createContact.mutateAsync({
        phone_number: trimmedPhone,
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
      })
      toast.success(t('contacts.add.success', 'Contact créé'))
      onCreated?.(created.phone_number || trimmedPhone)
      reset()
      onOpenChange(false)
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } })?.response?.data?.code
      toast.error(
        code === 'invalid_phone'
          ? t('contacts.add.invalidPhone', 'Ce numéro n’est pas valide. Pour l’étranger, commencez par + et l’indicatif du pays.')
          : t('contacts.add.error', 'Erreur lors de la création'),
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('contacts.add.title', 'Nouveau contact')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label htmlFor="addContactPhone">
              {t('contacts.labels.phone', 'Téléphone')}{' '}
              <span className="text-red-500">*</span>
            </Label>
            <Input
              id="addContactPhone"
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+1 514 555 1234"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="addContactFirstName">
                {t('contacts.labels.firstName', 'Prénom')}
              </Label>
              <Input
                id="addContactFirstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="addContactLastName">
                {t('contacts.labels.lastName', 'Nom')}
              </Label>
              <Input
                id="addContactLastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t('contacts.add.restOnProfile', 'Le reste se remplit sur la fiche, juste après.')}
          </p>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={createContact.isPending}
            >
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button type="submit" disabled={createContact.isPending}>
              {createContact.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t('contacts.add.submit', 'Créer')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
